// Hook personnalisé pour Google Analytics
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageTitle } from './usePageTitle';
import { 
  trackPageView, 
  trackEvent, 
  trackCTAClick, 
  trackFormSubmission,
  trackEstimateStep,
  trackTimeOnPage,
  AnalyticsEvent 
} from '../lib/analytics';

/**
 * Hook pour tracker automatiquement les changements de page
 */
export const usePageTracking = () => {
  const location = useLocation();
  const currentTitle = usePageTitle();
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    // Attendre que le titre soit mis à jour
    const timeoutId = setTimeout(() => {
      const pageTitle = document.title;
      const pagePath = location.pathname + location.search;
      const pageLocation = window.location.href;

      console.log('📊 GA4 Page View:', {
        title: pageTitle,
        path: pagePath,
        location: pageLocation
      });

      trackPageView({
        page_title: pageTitle,
        page_location: pageLocation,
        page_path: pagePath,
      });
    }, 100); // Petit délai pour s'assurer que le titre est mis à jour

    // Réinitialiser le timer pour la nouvelle page
    startTimeRef.current = Date.now();

    // Cleanup: tracker le temps passé sur la page précédente
    return () => {
      clearTimeout(timeoutId);
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      if (timeSpent > 5) { // Seulement si plus de 5 secondes
        trackTimeOnPage(location.pathname, timeSpent);
      }
    };
  }, [location, currentTitle]);
};

/**
 * Hook pour tracking manuel des pages avec titre personnalisé
 */
export const useManualPageTracking = () => {
  const trackPage = (title: string, path?: string) => {
    const pagePath = location.pathname + location.search;
    const pageLocation = window.location.href;

    console.log('📊 GA4 Manual Page View:', {
      title,
      path: path || pagePath,
      location: pageLocation
    });

    trackPageView({
      page_title: title,
      page_location: pageLocation,
      page_path: path || pagePath,
    });
  };

  return { trackPage };
};

/**
 * Hook pour les événements d'interaction
 */
export const useAnalyticsEvents = () => {
  const trackButtonClick = (buttonName: string, location: string) => {
    trackCTAClick(buttonName, location);
  };

  const trackFormEvent = (formName: string, success: boolean, errorMessage?: string) => {
    trackFormSubmission(formName, success);
    
    if (!success && errorMessage) {
      trackEvent({
        action: 'form_error_detail',
        category: 'errors',
        label: `${formName}_${errorMessage}`,
      });
    }
  };

  const trackEstimationEvent = (stepName: string, estimateType: 'quick' | 'detailed') => {
    trackEstimateStep(stepName, estimateType);
  };

  const trackCustomEvent = (event: AnalyticsEvent) => {
    trackEvent(event);
  };

  const trackExternalLink = (url: string, linkText: string) => {
    trackEvent({
      action: 'external_link_click',
      category: 'interactions',
      label: `${linkText}_${url}`,
    });
  };

  return {
    trackButtonClick,
    trackFormEvent,
    trackEstimationEvent,
    trackCustomEvent,
    trackExternalLink,
  };
};

/**
 * Hook pour tracker les erreurs de l'application
 */
export const useErrorTracking = () => {
  const trackError = (errorType: string, errorMessage: string, context?: string) => {
    trackEvent({
      action: 'application_error',
      category: 'errors',
      label: `${errorType}_${context || 'unknown'}_${errorMessage}`,
    });
  };

  const trackAPIError = (endpoint: string, statusCode: number, errorMessage: string) => {
    trackEvent({
      action: 'api_error',
      category: 'errors',
      label: `${endpoint}_${statusCode}_${errorMessage}`,
      value: statusCode,
    });
  };

  const trackPaymentError = (step: string, errorCode: string) => {
    trackEvent({
      action: 'payment_error',
      category: 'errors',
      label: `${step}_${errorCode}`,
    });
  };

  return {
    trackError,
    trackAPIError,
    trackPaymentError,
  };
};

/**
 * Hook pour tracker l'engagement utilisateur
 */
export const useEngagementTracking = () => {
  const trackScrollDepth = (depth: number, pagePath: string) => {
    trackEvent({
      action: 'scroll_depth',
      category: 'engagement',
      label: pagePath,
      value: depth,
    });
  };

  const trackFeatureUsage = (featureName: string, action: string) => {
    trackEvent({
      action: 'feature_usage',
      category: 'engagement',
      label: `${featureName}_${action}`,
    });
  };

  const trackUserFlow = (flowName: string, step: string, completed: boolean) => {
    trackEvent({
      action: completed ? 'flow_completed' : 'flow_abandoned',
      category: 'user_flow',
      label: `${flowName}_${step}`,
    });
  };

  return {
    trackScrollDepth,
    trackFeatureUsage,
    trackUserFlow,
  };
};